import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Search } from "lucide-react"
import { useState } from "react"
import { useLocation } from "wouter"

interface SearchBarProps {
  onSearch?: (filters: SearchFilters) => void
  className?: string
}

export interface SearchFilters {
  location: string
  priceRange: string
  propertyType: string
}

export function SearchBar({ onSearch, className = "" }: SearchBarProps) {
  const [, navigate] = useLocation()
  const [locationInput, setLocationInput] = useState("")
  const [priceRange, setPriceRange] = useState("")
  const [propertyType, setPropertyType] = useState("")

  const handleSearch = () => {
    if (onSearch) {
      onSearch({ location: locationInput, priceRange, propertyType })
    } else {
      // Navigate to browse page with query params
      const params = new URLSearchParams()

      // Use "search" parameter to match browse.tsx filters
      if (locationInput) params.set("search", locationInput)

      // Parse price range and set minPrice/maxPrice
      if (priceRange) {
        const [min, max] = priceRange.split("-")
        if (min) params.set("minPrice", min)
        if (max) params.set("maxPrice", max)
      }

      // Use "propertyType" to match browse.tsx filters
      if (propertyType) params.set("propertyType", propertyType)

      const url = `/browse?${params.toString()}`
      console.log("Navigating to:", url) // Debug log
      navigate(url)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch()
    }
  }

  return (
    <div className={`w-full ${className}`}>
      <div className="bg-card rounded-2xl shadow-xl p-2 md:p-3">
        <div className="flex flex-col md:flex-row gap-2">
          {/* Location Input */}
          <div className="flex-1 md:min-w-0">
            <Input
              type="text"
              placeholder="Location (e.g., Manila, Cebu)"
              value={locationInput}
              onChange={(e) => setLocationInput(e.target.value)}
              onKeyPress={handleKeyPress}
              className="h-12 md:h-14 bg-background border-0 text-base"
              data-testid="input-search-location"
            />
          </div>

          {/* Price Range Select */}
          {/* Price Range Select */}
          <div className="w-full md:w-48">
            <Select value={priceRange} onValueChange={setPriceRange}>
              <SelectTrigger
                aria-label="Select price range"
                className="h-12 md:h-14 bg-background border-0 text-base"
                data-testid="select-price-range"
              >
                <SelectValue placeholder="Price Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0-5000000">Under ₱5M</SelectItem>
                <SelectItem value="5000000-10000000">₱5M - ₱10M</SelectItem>
                <SelectItem value="10000000-20000000">₱10M - ₱20M</SelectItem>
                <SelectItem value="20000000-50000000">₱20M - ₱50M</SelectItem>
                <SelectItem value="50000000-999999999">Above ₱50M</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Property Type Select */}
          <div className="w-full md:w-48">
            <Select value={propertyType} onValueChange={setPropertyType}>
              <SelectTrigger
                aria-label="Select property type"
                className="h-12 md:h-14 bg-background border-0 text-base"
                data-testid="select-property-type"
              >
                <SelectValue placeholder="Property Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="house">House</SelectItem>
                <SelectItem value="condo">Condo</SelectItem>
                <SelectItem value="apartment">Apartment</SelectItem>
                <SelectItem value="townhouse">Townhouse</SelectItem>
                <SelectItem value="land">Land</SelectItem>
                <SelectItem value="commercial">Commercial</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Search Button */}
          <Button
            onClick={handleSearch}
            size="lg"
            className="h-12 md:h-14 px-8 text-base font-medium hover-elevate active-elevate-2"
            data-testid="button-search"
          >
            <Search className="h-5 w-5 mr-2" />
            Search
          </Button>
        </div>
      </div>
    </div>
  )
}
